/**
 * The page every configuration screen renders inside: Salesforge's Settings.
 *
 * ONE PAGE, ONE TAB STRIP. The reference draws Settings as the gear, the word
 * and a strip of tabs under it, and each tab is a section of the same page. So
 * this is `Page` titled Settings with the strip as its tabs, and each settings
 * route renders in the content area under it. There is no rail and no `More`
 * menu: the twelve tabs are listed in `settings-tabs-model.ts`, in the reference's
 * order first and ours after.
 *
 * Every path is unchanged, so each tab is a route and a bookmark, a runbook
 * line or an old message still lands on the same screen. `/settings` itself is
 * Usage, as the reference's bare settings address is.
 *
 * THE SCREEN'S OWN HEADER IS A SECTION HEADER. A screen rendered here that
 * still draws a `PageHeader` (the device engine does) gets it drawn as the
 * section title the reference puts inside the content, 18/600 with the grey
 * line under it, because the pane is wrapped in `SectionHeaders`. Screens
 * rebuilt for this page draw `SettingsSection` instead, which is the same title
 * with the action beside both lines, as Salesforge's are.
 *
 * WHAT IT DOES NOT DO: it does not gate anything. `adminOnly` hides a tab from
 * somebody who cannot use it, which is a courtesy, and the routes keep their
 * own `RequireAdmin` and the API keeps its own check. Three layers, and only
 * the last one is load bearing. The Prospecting tab is offered on a seat in
 * Prospecting rather than on the admin role, and its screen asks the service
 * the same question itself. The Operator tab is offered the same way, on a
 * seat in Operator.
 */
import { Outlet, useLocation } from 'react-router-dom'
import { IconSettings, Page, SectionHeaders } from '../../components'
import { useSession } from '../../lib/session'
import { useProspectingSeat } from '../prospecting/shared/useProspectingSeat'
import { useOperatorReach } from '../operator/shared/useOperatorMe'
import { PageTabStrip } from './PageTabStrip'
import { activeSettingsTab, tabsFor } from './settings-tabs-model'

export function SettingsLayout(): JSX.Element {
  const { isAdmin } = useSession()
  const { pathname } = useLocation()
  // The Prospecting tab is for admins and for anybody with a seat there.
  const prospecting = useProspectingSeat()
  // And the Operator tab, for Operator admins (a GojiB admin is one): a member
  // with a seat has nothing to set there.
  const operator = useOperatorReach().admin
  const tabs = tabsFor(isAdmin, { prospecting, operator })
  const active = activeSettingsTab(pathname, tabs)

  return (
    <Page
      icon={<IconSettings />}
      title="Settings"
      tabStrip={
        <PageTabStrip
          label="Settings"
          active={active}
          tabs={tabs.map((tab) => ({ key: tab.to, to: tab.to, label: tab.label, help: tab.help }))}
        />
      }
    >
      {/*
        `min-w-0` is not decoration. The pane holds tables that scroll
        sideways, and a flex child defaults to `min-width: auto`, which lets
        the widest table push the whole layout wider than the window.
      */}
      <div className="min-w-0">
        <SectionHeaders>
          <Outlet />
        </SectionHeaders>
      </div>
    </Page>
  )
}
